// Centro Médico → Catálogos → Presidentes de Misión (/medical/presidentes).
//
// Es el alta de usuarios de siempre, pero acotada: la empresa es la de la
// sesión y el perfil es siempre «presidente». Ninguno de los dos se elige, así
// que no se puede crear por descuido un presidente en otra empresa ni con el
// perfil de otro rol.
//
// Lo que sí se elige aquí son sus MISIONES: sin ellas el portal «Mis pacientes»
// le sale vacío, porque el alcance lo pone la base (script 853) y sin misiones
// asignadas no se ve ni un paciente.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Pencil, Plus, ShieldCheck, Target, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { usersAPI } from '@/lib/api/users.api';
import { useAuth } from '@/lib/hooks';
import { Campo, Modal, Switch, auth, fieldCls } from '@/features/admin/shared';
import { medRpc } from './shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';

const PERFIL = 'presidente';

interface UsuarioRow {
  id: string; username: string; email: string; activo: boolean;
  empresa_id: string; perfil_id: string;
}
interface MisionOpt { id: string; codigo: string | null; nombre: string; activa: boolean }

const VACIO = { username: '', email: '', password: '', activo: true, misiones: [] as string[] };

export function PresidentesMision() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? '';
  const [abierto, setAbierto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...VACIO });

  // El perfil no se elige: se resuelve por nombre y se usa siempre el mismo.
  const { data: perfil } = useQuery<{ id: string; nombre: string; nivel_acceso: number } | null>({
    queryKey: ['perfil-presidente'],
    queryFn: async () => (await auth.list<{ id: string; nombre: string; nivel_acceso: number }>(
      'perfiles', `?select=id,nombre,nivel_acceso&nombre=ilike.${PERFIL}&limit=1`))[0] ?? null,
    staleTime: Infinity,
  });

  const { data: presidentes = [], isFetching } = useQuery<UsuarioRow[]>({
    queryKey: ['med-presidentes', perfil?.id],
    queryFn: () => auth.list<UsuarioRow>('usuarios',
      `?select=id,username,email,activo,empresa_id,perfil_id&perfil_id=eq.${perfil!.id}&order=username`),
    enabled: !!perfil?.id,
    staleTime: 0, refetchOnMount: 'always',
  });

  // Las misiones de la empresa (las mismas que ofrece la ficha del paciente).
  const { data: misiones = [] } = useQuery<MisionOpt[]>({
    queryKey: ['med-misiones', false],
    queryFn: () => medRpc<MisionOpt[]>('med_misiones', { _incluir_archivadas: false }),
  });

  // Cuántas misiones tiene cada uno, para verlo en el listado.
  const { data: asignadas = {} } = useQuery<Record<string, string[]>>({
    queryKey: ['med-presidentes-misiones', presidentes.map((p) => p.id).join(',')],
    enabled: presidentes.length > 0,
    queryFn: async () => {
      const out: Record<string, string[]> = {};
      for (const p of presidentes) {
        try { out[p.id] = (await usersAPI.getCentrosCosto(p.id)).map((c) => c.id); }
        catch { out[p.id] = []; }
      }
      return out;
    },
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['med-presidentes'] });
    void qc.invalidateQueries({ queryKey: ['med-presidentes-misiones'] });
  };

  const abrirNuevo = () => { setEditId(null); setForm({ ...VACIO }); setAbierto(true); };
  const abrirEditar = (u: UsuarioRow) => {
    setEditId(u.id);
    setForm({ username: u.username, email: u.email, password: '', activo: u.activo,
              misiones: asignadas[u.id] ?? [] });
    setAbierto(true);
    usersAPI.getCentrosCosto(u.id)
      .then((cc) => setForm((f) => ({ ...f, misiones: cc.map((c) => c.id) })))
      .catch(() => undefined);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      if (!perfil?.id) throw new Error('No existe el perfil «presidente» en el sistema');
      let uid = editId;
      if (editId) {
        await auth.update('usuarios', editId, {
          username: form.username, email: form.email, activo: form.activo,
          // El perfil se reafirma en cada guardado: si alguien lo cambió por
          // otra vía, esta pantalla lo devuelve a su sitio.
          perfil_id: perfil.id, updated_at: new Date().toISOString(),
        });
        if (form.password.trim()) {
          await apiClient.post('/rpc/admin_set_password', { _user_id: editId, _password: form.password });
        }
      } else {
        const { data } = await apiClient.post('/rpc/register', {
          _username: form.username, _email: form.email, _password: form.password,
          _empresa_id: empresaId, _perfil_id: perfil.id,
        });
        uid = (data as { user?: { id?: string }; id?: string })?.user?.id
           ?? (data as { id?: string })?.id ?? null;
      }
      if (uid) {
        try { await usersAPI.setCentrosCosto(uid, form.misiones); }
        catch { toast.warning('Presidente guardado, pero no se pudieron asignar las misiones'); }
      }
    },
    onSuccess: () => {
      invalidar(); setAbierto(false);
      toast.success(editId ? 'Presidente actualizado' : 'Presidente creado');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const toggle = (id: string) =>
    setForm((f) => ({ ...f, misiones: f.misiones.includes(id)
      ? f.misiones.filter((x) => x !== id) : [...f.misiones, id] }));

  const accessor = (u: UsuarioRow, col: string) => {
    switch (col) {
      case 'username': return u.username;
      case 'email': return u.email;
      case 'misiones': return (asignadas[u.id] ?? []).length;
      case 'estado': return u.activo ? 1 : 0;
      default: return null;
    }
  };
  const sort = useSort(presidentes, accessor, 'username');

  const nombreMision = (id: string) =>
    misiones.find((m) => m.id === id)?.nombre ?? '—';

  return (
    <div className="p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <UserCog className="h-5 w-5 text-primary" /> Presidentes de Misión
          </h1>
          <p className="text-sm text-muted-foreground">
            {user?.empresa?.nombre} · perfil <b>{perfil?.nombre ?? PERFIL}</b>
          </p>
        </div>
        <Button onClick={abrirNuevo} disabled={!perfil?.id}>
          <Plus className="mr-1 h-4 w-4" /> Nuevo presidente
        </Button>
      </div>

      {!perfil && (
        <Card className="mb-3 border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="py-3 text-sm text-amber-900 dark:text-amber-100">
            No existe el perfil «presidente» en el sistema. Créalo en
            Sistemas → Gestión de Autenticación → Perfiles antes de dar de alta a nadie.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{sort.sorted.length} presidente(s)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="w-10 py-2 pr-3 text-right">N°</th>
                  <SortTh col="username" sort={sort}>Usuario</SortTh>
                  <SortTh col="email" sort={sort}>Email</SortTh>
                  <th className="py-2 pr-3">Misiones</th>
                  <SortTh col="misiones" sort={sort} align="right">N°</SortTh>
                  <SortTh col="estado" sort={sort} align="center">Estado</SortTh>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {isFetching && sort.sorted.length === 0 && (
                  <tr><td colSpan={7} className="py-6 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" />
                  </td></tr>
                )}
                {sort.sorted.map((u, i) => (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{u.username}</td>
                    <td className="py-2 pr-3">{u.email}</td>
                    <td className="py-2 pr-3">
                      {(asignadas[u.id] ?? []).length === 0
                        ? <span className="text-amber-700 dark:text-amber-400">
                            sin misiones — no verá pacientes
                          </span>
                        : <span className="text-xs">
                            {(asignadas[u.id] ?? []).map(nombreMision).join(' · ')}
                          </span>}
                    </td>
                    <td className="py-2 pr-3 text-right">{(asignadas[u.id] ?? []).length}</td>
                    <td className="py-2 pr-3 text-center">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${u.activo
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-muted text-muted-foreground'}`}>
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <Button size="sm" variant="ghost" title="Editar" onClick={() => abrirEditar(u)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {sort.sorted.length > 0 && (
                  <tr className="border-t-2 bg-muted/30 font-semibold">
                    <td className="py-2 pr-3" colSpan={4}>Totales ({sort.sorted.length})</td>
                    <td className="py-2 pr-3 text-right">
                      {sort.sorted.reduce((a, u) => a + (asignadas[u.id] ?? []).length, 0)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                )}
                {sort.sorted.length === 0 && !isFetching && (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">
                    Todavía no hay presidentes de misión.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar presidente' : 'Nuevo presidente de misión'}
               onClose={() => setAbierto(false)} wide>
          <div className="space-y-3">
            {/* Empresa y perfil: se muestran, no se eligen. */}
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Empresa">
                <div className={`${fieldCls} flex items-center bg-muted/50 text-muted-foreground`}>
                  {user?.empresa?.nombre ?? '—'}
                </div>
              </Campo>
              <Campo label="Perfil">
                <div className={`${fieldCls} flex items-center gap-2 bg-muted/50 text-muted-foreground`}>
                  <ShieldCheck className="h-4 w-4" /> {perfil?.nombre ?? PERFIL}
                </div>
              </Campo>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Campo label="Usuario *">
                <input className={fieldCls} value={form.username}
                       onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </Campo>
              <Campo label="Email *">
                <input className={fieldCls} type="email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Campo>
            </div>

            <Campo label={editId ? 'Nueva contraseña (opcional)' : 'Contraseña *'}>
              <div className="relative">
                <KeyRound className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input type="password" className={`${fieldCls} pl-9`} value={form.password}
                       placeholder={editId ? 'Dejar vacío para no cambiar' : ''}
                       onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            </Campo>

            <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label="Activo" />

            <Campo label="Misiones que puede consultar">
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-input p-2">
                {misiones.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No hay misiones activas. Créalas en Catálogos → Misiones.
                  </p>
                )}
                {misiones.map((m) => (
                  <label key={m.id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" className="h-4 w-4 rounded border-input"
                           checked={form.misiones.includes(m.id)} onChange={() => toggle(m.id)} />
                    <Target className="h-3.5 w-3.5 text-muted-foreground" />
                    {m.codigo ? `${m.codigo} · ` : ''}{m.nombre}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {form.misiones.length} seleccionada(s).
                {form.misiones.length === 0 && ' Sin ninguna, su portal saldrá vacío.'}
              </p>
            </Campo>

            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending}
                      disabled={!form.username.trim() || !form.email.trim()
                                || (!editId && !form.password.trim()) || !perfil?.id}
                      onClick={() => guardar.mutate()}>
                {editId ? 'Guardar' : 'Crear presidente'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
